import type * as React from 'react'
import { ExternalLink } from 'lucide-react'
import { reviewSiteName, reviewSiteNamed } from '@lib/helpers/review-sites'

/** Google's own steps for the review link, from its Business Profile help. */
export const GOOGLE_REVIEW_LINK_HELP = 'https://support.google.com/business/answer/16816815'

const ANY_SITE = 'Open your page on the site and copy the address of its Write a review button. If it has none, your page’s address works.'

/**
 * How to find the right address on each site, under the address box: by the
 * site the typed address is on, or the site its name says before an address
 * is typed. Each says where the venue finds its own link, and Yelp's says why
 * a venue may not want one.
 */
export function reviewLinkHint(link: { name: string; url: string }): React.ReactNode {
  const site = reviewSiteName(link.url.trim()) ?? reviewSiteNamed(link.name)
  switch (site) {
    case 'Google':
      return (
        <>
          Search for your venue on Google while signed in as its owner, choose Read reviews, then Get more reviews, and copy the link it
          gives you.{' '}
          <a
            href={GOOGLE_REVIEW_LINK_HELP}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-accent-ink underline-offset-2 hover:underline"
          >
            Google’s steps
            <ExternalLink className="h-3 w-3" aria-hidden />
          </a>
        </>
      )
    case 'Facebook':
      return 'Open your page’s Reviews tab (it may say Recommendations) and copy its address. No tab? Turn reviews on in the page’s settings.'
    case 'Yelp':
      return 'Use your public Yelp page. Yelp asks businesses not to request reviews, and may hide reviews it thinks were requested.'
    case 'The Knot':
    case 'WeddingWire':
      return `In your vendor dashboard on ${site}, find the review link for past clients and copy it. Couples need an account on ${site} to post.`
    case null:
      return ANY_SITE
    default:
      return `Open your page on ${site} and copy the address of its Write a review button. If it has none, your page’s address works.`
  }
}
